import React, { useState, useEffect } from "react";
import { FaHashtag, FaFolder, FaSitemap } from "react-icons/fa";
import apiRequest from "../services/api";

const AccountChartManagement = () => {
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Fetch Accounts List Matrix based on findByTenantId() view context
  const fetchAccounts = async () => {
    setIsLoading(true);
    setError("");
    try {
      const resp = await apiRequest("settings/accounts");
      setAccounts(resp.data || []);
    } catch (err) {
      setError(err?.message || "Failed to load active account chart matrices.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  // Helper color map pill indicators based on financial segmentations
  const getTypePillStyle = (type) => {
    switch (type) {
      case "ASSET":
        return "bg-emerald-950/40 border-emerald-900/40 text-emerald-400";
      case "LIABILITY":
        return "bg-amber-950/40 border-amber-900/40 text-amber-400";
      case "EQUITY":
        return "bg-purple-950/40 border-purple-900/40 text-purple-400";
      case "REVENUE":
        return "bg-emerald-950/40 border-emerald-900/40 text-emerald-400";
      case "COGS":
        return "bg-orange-950/40 border-orange-900/40 text-orange-400";
      default:
        return "bg-slate-950/40 border-slate-900/40 text-slate-400";
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 text-slate-100 relative min-h-screen pb-24">
      {/* Module Component Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-slate-900/50 p-6 rounded-2xl border border-slate-800 backdrop-blur-md">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-white flex items-center gap-2">
            <FaSitemap className="text-emerald-500" /> Chart of Accounts
          </h1>
        </div>
      </div>

      {/* Error Output Portal */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-xs font-medium">
          {error}
        </div>
      )}

      {/* Main Structural Data Grid Ledger */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-950/40 text-slate-400 text-[11px] font-bold tracking-wider uppercase">
                <th className="px-6 py-4">Account Name</th>

                <th className="px-6 py-4">Type</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800 text-slate-300 text-xs">
              {isLoading ? (
                <tr>
                  <td colSpan="3" className="text-center py-8 text-slate-500">
                    Querying...
                  </td>
                </tr>
              ) : accounts.length === 0 ? (
                <tr>
                  <td colSpan="3" className="text-center py-8 text-slate-500">
                    No accounts registered.
                  </td>
                </tr>
              ) : (
                accounts.map((account) => (
                  <tr
                    key={account.id}
                    className="hover:bg-slate-800/30 transition-colors"
                  >
                    <td className="px-6 py-4 font-mono font-bold text-white flex items-center gap-2">
                      <FaHashtag className="text-slate-500 text-[10px]" />
                      <span>{account.name}</span>
                    </td>

                    <td className="px-6 py-4">
                      <span
                        className={`px-2.5 py-1 rounded text-[10px] font-bold border ${getTypePillStyle(
                          account.type
                        )}`}
                      >
                        {account.type}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AccountChartManagement;
